import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography, IconButton,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { SupplierService } from '../../services/supplier.service';
import { Supplier } from '../../interfaces/procurement/supplier.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';

// SupplierController's write endpoints (@PreAuthorize StoreAdmin, InstitutionAdmin). Accountant may read
// suppliers but would get a 403 on create/update/delete.
const WRITE_ROLES = ['StoreAdmin', 'InstitutionAdmin'];
const currentRoles = (): string[] => {
  const raw = AuthService.getCurrentUser()?.roles;
  return Array.isArray(raw) ? raw : raw ? [raw] : [];
};

export const SupplierList: React.FC = () => {
  const navigate = useNavigate();
  const canWrite = currentRoles().some(r => WRITE_ROLES.includes(r));
  const toast = useToast();
  const [rows, setRows] = useState<Supplier[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    try {
      setLoading(true);
      const res = await SupplierService.listByInstitution(page, size);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Delete this supplier?')) return;
    try {
      await SupplierService.delete(id);
      toast.showSuccess('Supplier deleted');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to delete supplier');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Suppliers</Typography>
        {canWrite && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procurement/suppliers/new')}>
            New Supplier
          </Button>
        )}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Tax Reg No</TableCell>
                {canWrite && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/procurement/suppliers/${r.id}`)}
                >
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.contactPerson ?? '—'}</TableCell>
                  <TableCell>{r.email ?? '—'}</TableCell>
                  <TableCell>{r.phone ?? '—'}</TableCell>
                  <TableCell>{r.taxRegistrationNo ?? '—'}</TableCell>
                  {canWrite && (
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); navigate(`/procurement/suppliers/${r.id}/edit`); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => handleDelete(r.id, e)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};
